import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5159';

interface Gorsel { url: string; }
interface OdemePlanKalemi { vadeTarihi: string; miktar: number; aciklama?: string; }
interface Detaylar {
  proje?: string;
  blok?: string;
  kat?: string;
  cephe?: string;
  manzara?: string;
  takipNo?: string;
  adres?: string;
  il?: string;
  ilce?: string;
  mahalle?: string;
}
interface TeklifItem {
  teklifID: number;
  teklifAdi: string;
  siraNo: number;
  teklifMiktari?: number;
  ortalamaVadeGun?: number;
  odemePlanKalemleri: OdemePlanKalemi[];
}
interface PortalItem {
  gayrimenkulID: number;
  baslik: string;
  altBaslik: string;
  tur: string;
  fiyat?: number;
  m2?: number;
  brutM2?: number;
  durum: string;
  gorseller: Gorsel[];
  ozellikler?: { ad: string }[];
  detaylar?: Detaylar;
  teklifler?: TeklifItem[];
  musteriNotu?: string;
  musteriDurumu?: string;
}
interface PortalData {
  linkID: number;
  sunumTuru: string;
  baslik: string;
  cariAdSoyad?: string;
  gecerlilikTarihi: string;
  sunumTakipNo?: string;
  items: PortalItem[];
  projeBelgeleri?: {
    gorseller: { url: string; aciklama?: string }[];
    kataloglar: { url: string; ad: string; aciklama?: string }[];
    videolar: { url: string; ad: string; aciklama?: string }[];
  };
  paylasimDokumanlar?: {
    dokumanID: number;
    baslik: string;
    aciklama?: string;
    dokumanTipi: string;
    dosyaYolu?: string;
    videoEmbedKodu?: string;
    orijinalDosyaAdi?: string;
    etiketler: { ad: string; renk: string }[];
  }[];
}

const formatPara = (val?: number) =>
  val != null ? val.toLocaleString('tr-TR') + ' ₺' : '-';

const formatTarih = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });

export default function SunumPortal() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PortalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [expandedPlans, setExpandedPlans] = useState<Set<number>>(new Set());
  const [activeTeklifTab, setActiveTeklifTab] = useState<Record<number, number>>({});
  const [photoIdx, setPhotoIdx] = useState<Record<number, number>>({});
  const [touchStart, setTouchStart] = useState<Record<number, number>>({});
  const [projePhotoIdx, setProjePhotoIdx] = useState(0);
  const [projeTouchStart, setProjeTouchStart] = useState(0);
  const [notes, setNotes] = useState<Record<number, { durum: string; not: string }>>({});
  const [saving, setSaving] = useState<number | null>(null);
  const [saved, setSaved] = useState<Set<number>>(new Set());

  // Online Görüşme
  const [activeGorusme, setActiveGorusme] = useState<{gorusmeID: number; odaAdi: string; jitsiUrl: string} | null>(null);
  const [showGorusmeBanner, setShowGorusmeBanner] = useState(false);
  const [gorusmeKatilimciId, setGorusmeKatilimciId] = useState<number | null>(null);
  const [showJitsi, setShowJitsi] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars

  // Döküman viewer modal
  const [dokumanBolumAcik, setDokumanBolumAcik] = useState(false);
  const [dokumanViewer, setDokumanViewer] = useState<{tip: 'pdf'|'gorsel'|'video'; url?: string; embed?: string; baslik: string} | null>(null);

  const aciklaViewer = (doc: NonNullable<PortalData['paylasimDokumanlar']>[0]) => {
    const ext = (doc.dosyaYolu || '').split('.').pop()?.toLowerCase() || '';
    const gorselExt = ['jpg','jpeg','png','gif','webp','bmp'];
    const isVideo = doc.dokumanTipi?.toLowerCase() === 'video' || !!doc.videoEmbedKodu;
    if (isVideo && doc.videoEmbedKodu) {
      setDokumanViewer({ tip: 'video', embed: doc.videoEmbedKodu, baslik: doc.baslik });
    } else if (gorselExt.includes(ext)) {
      setDokumanViewer({ tip: 'gorsel', url: API_BASE + doc.dosyaYolu, baslik: doc.baslik });
    } else {
      setDokumanViewer({ tip: 'pdf', url: API_BASE + doc.dosyaYolu, baslik: doc.baslik });
    }
  };

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/SunumMusteriLinks/portal/${token}`)
      .then(async r => {
        const json = await r.json();
        if (!r.ok) throw new Error(json.message || 'Hata oluştu.');
        return json as PortalData;
      })
      .then(d => {
        setData(d);
        const init: Record<number, { durum: string; not: string }> = {};
        d.items.forEach(item => {
          init[item.gayrimenkulID] = {
            durum: item.musteriDurumu || '',
            not: item.musteriNotu || ''
          };
        });
        setNotes(init);
      })
      .catch(e => setError(e.message || 'Yükleme hatası'))
      .finally(() => setLoading(false));
  }, [token]);

  // Aktif online görüşme kontrolü — 30 saniyede bir yokla
  useEffect(() => {
    if (!data?.sunumTakipNo) return;
    const takipNo = data.sunumTakipNo;
    const checkGorusme = () => {
      fetch(`${API_BASE}/api/OnlineGorusmeler/aktif/${takipNo}`)
        .then(r => r.ok ? r.json() : null)
        .then(g => {
          if (g && g.gorusmeID) {
            setActiveGorusme({ gorusmeID: g.gorusmeID, odaAdi: g.odaAdi, jitsiUrl: g.jitsiUrl });
            setShowGorusmeBanner(true);
          } else {
            setActiveGorusme(null);
            if (!showJitsi) setShowGorusmeBanner(false);
          }
        })
        .catch(() => {});
    };
    checkGorusme();
    const interval = setInterval(checkGorusme, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.sunumTakipNo]);

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const togglePlan = (id: number) => {
    setExpandedPlans(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleGorusmeKatil = async () => {
    if (!activeGorusme) return;
    try {
      const res = await fetch(`${API_BASE}/api/OnlineGorusmeler/${activeGorusme.gorusmeID}/katil`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adSoyad: data?.cariAdSoyad || null })
      });
      if (res.ok) {
        const json = await res.json();
        setGorusmeKatilimciId(json.katilimciID);
      }
    } catch { /* hata sessizce geç */ }
    setShowGorusmeBanner(false);
    const w = window.screen.width;
    const h = window.screen.height;
    window.open(
      `https://meet.jit.si/${activeGorusme.odaAdi}`,
      'JitsiGorusme',
      `toolbar=no,menubar=no,location=no,status=no,scrollbars=no,resizable=yes,width=${Math.round(w * 0.85)},height=${Math.round(h * 0.85)},left=${Math.round(w * 0.075)},top=${Math.round(h * 0.075)}`
    );
  };

  const handleGorusmeAyril = async () => {
    if (gorusmeKatilimciId) {
      fetch(`${API_BASE}/api/OnlineGorusmeler/katilimci/${gorusmeKatilimciId}/ayril`, { method: 'PATCH' }).catch(() => {});
    }
    setShowJitsi(false);
    setGorusmeKatilimciId(null);
  };

  const handleTouchStart = (id: number, e: React.TouchEvent) => {
    setTouchStart(prev => ({ ...prev, [id]: e.touches[0].clientX }));
  };

  const handleTouchEnd = (id: number, len: number, e: React.TouchEvent) => {
    const startX = touchStart[id];
    if (startX == null) return;
    const endX = e.changedTouches[0].clientX;
    const diff = startX - endX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextPhoto(id, len);
      else prevPhoto(id, len);
    }
  };

  const prevPhoto = useCallback((id: number, len: number) => {
    setPhotoIdx(prev => ({ ...prev, [id]: ((prev[id] ?? 0) - 1 + len) % len }));
  }, []);

  const nextPhoto = useCallback((id: number, len: number) => {
    setPhotoIdx(prev => ({ ...prev, [id]: ((prev[id] ?? 0) + 1) % len }));
  }, []);

  const handleProjeTouchStart = (e: React.TouchEvent) => {
    setProjeTouchStart(e.touches[0].clientX);
  };

  const handleProjeTouchEnd = (len: number, e: React.TouchEvent) => {
    const endX = e.changedTouches[0].clientX;
    const diff = projeTouchStart - endX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextProjePhoto(len);
      else prevProjePhoto(len);
    }
  };

  const prevProjePhoto = (len: number) => {
    setProjePhotoIdx(prev => (prev - 1 + len) % len);
  };

  const nextProjePhoto = (len: number) => {
    setProjePhotoIdx(prev => (prev + 1) % len);
  };

  const setNote = (id: number, field: 'durum' | 'not', val: string) => {
    setNotes(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));
  };

  const saveNote = async (id: number) => {
    if (!token) return;
    setSaving(id);
    try {
      const note = notes[id] || { durum: '', not: '' };
      const res = await fetch(`${API_BASE}/api/SunumMusteriLinks/portal/${token}/gayrimenkul/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ musteriNotu: note.not || null, musteriDurumu: note.durum || null })
      });
      if (!res.ok) throw new Error('Kayıt hatası');
      setSaved(prev => new Set(prev).add(id));
      setTimeout(() => setSaved(prev => { const n = new Set(prev); n.delete(id); return n; }), 2500);
    } catch {
      alert('Görüşünüz kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSaving(null);
    }
  };

  const daysLeft = data
    ? Math.ceil((new Date(data.gecerlilikTarihi).getTime() - Date.now()) / 86400000)
    : null;

  // ---- Render ----

  if (loading) return (
    <div style={styles.center}>
      <i className="fas fa-spinner fa-spin" style={{ fontSize: 32, color: '#e67d21' }} />
      <p style={{ marginTop: 12, color: '#666' }}>Yükleniyor...</p>
    </div>
  );

  if (error) return (
    <div style={styles.center}>
      <i className="fas fa-exclamation-circle" style={{ fontSize: 48, color: '#dc3545' }} />
      <h3 style={{ marginTop: 16, color: '#333' }}>Erişim Sorunu</h3>
      <p style={{ color: '#666', textAlign: 'center', maxWidth: 300 }}>{error}</p>
    </div>
  );

  if (!data) return null;

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.brand}>
            <i className="fas fa-building" style={{ marginRight: 8, color: '#e67d21' }} />
            <span style={styles.brandText}>YapiCRM</span>
          </div>
          <div style={{ flex: 1 }}>
            <div style={styles.sunumBaslik}>{data.baslik}</div>
            {data.cariAdSoyad && (
              <div style={styles.cariAd}>
                <i className="fas fa-user" style={{ marginRight: 5, fontSize: 11 }} />
                {data.cariAdSoyad}
              </div>
            )}
          </div>
          <div style={styles.expiry}>
            <i className="fas fa-clock" style={{ marginRight: 4 }} />
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ fontSize: 10, opacity: 0.75 }}>Geçerlilik</div>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{formatTarih(data.gecerlilikTarihi)}</div>
            </div>
          </div>
        </div>
        {daysLeft !== null && daysLeft <= 1 && (
          <div style={styles.expiryWarning}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }} />
            {daysLeft <= 0 ? 'Bu linkin geçerlilik süresi dolmuştur.' : 'Bu link 1 gün içinde sona erecek.'}
          </div>
        )}
      </header>

      {/* Canlı Görüşme Banner */}
      {showGorusmeBanner && activeGorusme && !showJitsi && (
        <div style={{
          background: 'linear-gradient(135deg, #1a2440 0%, #2d3a5c 100%)',
          color: '#fff',
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#22c55e', display: 'inline-block', boxShadow: '0 0 0 3px rgba(34,197,94,0.3)', animation: 'pulse 1.5s infinite' }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Bu sunum için canlı görüşme talebi bulunuyor</div>
              <div style={{ fontSize: 12.5, opacity: 0.8, marginTop: 2 }}>Temsilcinizle görüşmek istiyor musunuz?</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handleGorusmeKatil}
              style={{
                background: '#22c55e', color: '#fff', border: 'none', borderRadius: 8,
                padding: '9px 20px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
              }}
            >
              <i className="fas fa-video" style={{ marginRight: 6 }} />
              Görüşmeye Katıl
            </button>
            <button
              onClick={() => setShowGorusmeBanner(false)}
              style={{
                background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 8, padding: '9px 16px', fontSize: 13, cursor: 'pointer',
              }}
            >
              Daha Sonra
            </button>
          </div>
        </div>
      )}

      {/* Jitsi Görüşme Penceresi */}
      {showJitsi && activeGorusme && (
        <div style={{ background: '#0f172a', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
          <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>Canlı Görüşme başlatıldı — Temsilcinizle görüşebilirsiniz.</span>
        </div>
      )}

      {/* Proje Belgeleri Section */}
      {data.projeBelgeleri && (data.projeBelgeleri.gorseller.length > 0 || data.projeBelgeleri.kataloglar.length > 0 || data.projeBelgeleri.videolar.length > 0) && (
        <div style={styles.projeBelgeleriWrap}>
          <div style={styles.projeBelgeleriContainer}>
            <div style={styles.projeBelgeleriTitle}>
              <i className="fas fa-folder-open" style={{ marginRight: 8, color: '#e67d21' }} />
              Proje Dokümanları
            </div>

            {/* Proje Görselleri */}
            {data.projeBelgeleri.gorseller.length > 0 && (
              <div style={styles.belgeBolum}>
                <div style={styles.belgeBolumBaslik}>
                  <i className="fas fa-images" style={{ marginRight: 6, color: '#3b82f6' }} />
                  Proje Görselleri
                </div>
                <div 
                  style={styles.projePhotoWrap}
                  onTouchStart={handleProjeTouchStart}
                  onTouchEnd={(e) => handleProjeTouchEnd(data.projeBelgeleri!.gorseller.length, e)}
                >
                  <div style={styles.projePhotoSlider}>
                    {data.projeBelgeleri.gorseller.map((g, i) => (
                      <div
                        key={i}
                        style={{
                          ...styles.projePhotoSlide,
                          transform: `translateX(-${projePhotoIdx * 100}%)`,
                        }}
                      >
                        <img 
                          src={`${API_BASE}${g.url}`} 
                          alt={g.aciklama || `Görsel ${i + 1}`}
                          style={styles.projePhotoImg}
                          onClick={() => window.open(`${API_BASE}${g.url}`, '_blank')}
                        />
                        {g.aciklama && <div style={styles.projePhotoAciklama}>{g.aciklama}</div>}
                      </div>
                    ))}
                  </div>
                  {data.projeBelgeleri.gorseller.length > 1 && (
                    <>
                      <button 
                        style={{ ...styles.projePhotoBtn, left: 8 }} 
                        onClick={() => prevProjePhoto(data.projeBelgeleri!.gorseller.length)}
                      >
                        <i className="fas fa-chevron-left" />
                      </button>
                      <button 
                        style={{ ...styles.projePhotoBtn, right: 8 }} 
                        onClick={() => nextProjePhoto(data.projeBelgeleri!.gorseller.length)}
                      >
                        <i className="fas fa-chevron-right" />
                      </button>
                      <div style={styles.projePhotoCount}>
                        {projePhotoIdx + 1} / {data.projeBelgeleri.gorseller.length}
                      </div>
                      <div style={styles.projePhotoDots}>
                        {data.projeBelgeleri.gorseller.map((_, i) => (
                          <span
                            key={i}
                            style={{
                              ...styles.projePhotoDot,
                              ...(i === projePhotoIdx ? styles.projePhotoDotActive : {})
                            }}
                            onClick={() => setProjePhotoIdx(i)}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Kataloglar */}
            {data.projeBelgeleri.kataloglar.length > 0 && (
              <div style={styles.belgeBolum}>
                <div style={styles.belgeBolumBaslik}>
                  <i className="fas fa-book" style={{ marginRight: 6, color: '#10b981' }} />
                  Tanıtım Katalogları
                </div>
                <div style={styles.belgeList}>
                  {data.projeBelgeleri.kataloglar.map((k, i) => (
                    <a
                      key={i}
                      href={`${API_BASE}${k.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.belgeLink}
                    >
                      <i className="fas fa-file-pdf" style={{ marginRight: 8, color: '#dc3545' }} />
                      <div style={{ flex: 1 }}>
                        <div style={styles.belgeLinkAd}>{k.ad}</div>
                        {k.aciklama && <div style={styles.belgeLinkAciklama}>{k.aciklama}</div>}
                      </div>
                      <i className="fas fa-download" style={{ color: '#888' }} />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Videolar */}
            {data.projeBelgeleri.videolar.length > 0 && (
              <div style={styles.belgeBolum}>
                <div style={styles.belgeBolumBaslik}>
                  <i className="fas fa-video" style={{ marginRight: 6, color: '#8b5cf6' }} />
                  Tanıtım Videoları
                </div>
                <div style={styles.belgeList}>
                  {data.projeBelgeleri.videolar.map((v, i) => (
                    <a
                      key={i}
                      href={`${API_BASE}${v.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.belgeLink}
                    >
                      <i className="fas fa-play-circle" style={{ marginRight: 8, color: '#e67d21' }} />
                      <div style={{ flex: 1 }}>
                        <div style={styles.belgeLinkAd}>{v.ad}</div>
                        {v.aciklama && <div style={styles.belgeLinkAciklama}>{v.aciklama}</div>}
                      </div>
                      <i className="fas fa-external-link-alt" style={{ color: '#888' }} />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Paylaşılan Dökümanlar */}
      {data.paylasimDokumanlar && data.paylasimDokumanlar.length > 0 && (
        <div style={{ margin: '0 16px 20px', background: '#fff', borderRadius: 14, boxShadow: '0 1px 8px rgba(0,0,0,0.09)', overflow: 'hidden', border: dokumanBolumAcik ? '1.5px solid #cd6906' : '1.5px solid transparent', transition: 'border 0.15s' }}>
          {/* Bölüm başlığı - accordion header */}
          <div
            onClick={() => setDokumanBolumAcik(p => !p)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', cursor: 'pointer', background: dokumanBolumAcik ? '#fff8f2' : '#fff', transition: 'background 0.15s' }}
          >
            <i className="fas fa-file-export" style={{ color: '#cd6906', fontSize: 16 }} />
            <span style={{ flex: 1, fontWeight: 700, fontSize: 15, color: '#1e293b' }}>Dökümanlar</span>
            <span style={{ background: '#cd6906', color: '#fff', borderRadius: 10, padding: '1px 9px', fontSize: 12, fontWeight: 700, marginRight: 4 }}>{data.paylasimDokumanlar.length}</span>
            <i className={`fas fa-chevron-${dokumanBolumAcik ? 'up' : 'down'}`} style={{ color: '#cd6906', fontSize: 13 }} />
          </div>
          {/* Döküman listesi */}
          {dokumanBolumAcik && (
            <div style={{ borderTop: '1px solid #f1ece7' }}>
              {data.paylasimDokumanlar.map((doc, idx) => {
                const ext = (doc.dosyaYolu || '').split('.').pop()?.toLowerCase() || '';
                const gorselExt = ['jpg','jpeg','png','gif','webp','bmp'];
                const isGorsel = gorselExt.includes(ext);
                const isVideo = doc.dokumanTipi?.toLowerCase() === 'video' || !!doc.videoEmbedKodu;
                const icon = isVideo ? 'fas fa-film' : isGorsel ? 'fas fa-image' : 'fas fa-file-pdf';
                const isLast = idx === data.paylasimDokumanlar!.length - 1;
                return (
                  <div key={doc.dokumanID} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', borderBottom: isLast ? 'none' : '1px solid #f8f0ea' }}>
                    <i className={icon} style={{ color: '#cd6906', fontSize: 14, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontWeight: 500, fontSize: 13.5, color: '#1e293b' }}>{doc.baslik}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      {(doc.dosyaYolu || (isVideo && doc.videoEmbedKodu)) && (
                        <button
                          onClick={() => aciklaViewer(doc)}
                          title="Görüntüle"
                          style={{ width: 32, height: 32, borderRadius: 8, background: '#cd6906', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}
                        >
                          <i className={isVideo ? 'fas fa-play' : 'fas fa-eye'} />
                        </button>
                      )}
                      {!isVideo && doc.dosyaYolu && (
                        <a
                          href={API_BASE + doc.dosyaYolu}
                          download={doc.orijinalDosyaAdi || doc.baslik}
                          title="İndir"
                          style={{ width: 32, height: 32, borderRadius: 8, background: '#0f9d6e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, textDecoration: 'none' }}
                        >
                          <i className="fas fa-download" />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Döküman Viewer Modal */}
      {dokumanViewer && (
        <div
          onClick={() => setDokumanViewer(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.82)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
        >
          {/* Header bar */}
          <div
            onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 900, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: 'rgba(0,0,0,0.55)' }}
          >
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{dokumanViewer.baslik}</span>
            <button onClick={() => setDokumanViewer(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>✕</button>
          </div>
          {/* Content */}
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 900, flex: 1, maxHeight: 'calc(90vh - 52px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 0 16px' }}>
            {dokumanViewer.tip === 'gorsel' && (
              <img src={dokumanViewer.url} alt={dokumanViewer.baslik} style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 10, boxShadow: '0 4px 32px rgba(0,0,0,0.4)' }} />
            )}
            {dokumanViewer.tip === 'pdf' && (
              <iframe src={dokumanViewer.url} title={dokumanViewer.baslik} style={{ width: '100%', height: '80vh', border: 'none', borderRadius: 10 }} />
            )}
            {dokumanViewer.tip === 'video' && dokumanViewer.embed && (
              <div style={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: dokumanViewer.embed }} />
            )}
          </div>
        </div>
      )}

      {/* Items */}
      <main style={styles.main}>
        {data.items.length === 0 ? (
          <div style={styles.emptyState}>
            <i className="fas fa-home" style={{ fontSize: 40, color: '#ccc' }} />
            <p style={{ color: '#888', marginTop: 12 }}>Gösterilecek gayrimenkul bulunamadı.</p>
          </div>
        ) : (
          data.items.map(item => {
            const idx = photoIdx[item.gayrimenkulID] ?? 0;
            const isOpen = expanded.has(item.gayrimenkulID);
            const isPlanOpen = expandedPlans.has(item.gayrimenkulID);
            const note = notes[item.gayrimenkulID] || { durum: '', not: '' };
            const isSaving = saving === item.gayrimenkulID;
            const isSaved = saved.has(item.gayrimenkulID);

            return (
              <div key={item.gayrimenkulID} style={styles.card}>
                {/* Accordion Header - Always Visible */}
                <div style={styles.accordionHeader} onClick={() => toggleExpand(item.gayrimenkulID)}>
                  <div style={{ flex: 1 }}>
                    <div style={styles.accordionTitle}>{item.baslik}</div>
                    {item.altBaslik && <div style={styles.accordionSub}>{item.altBaslik}</div>}
                  </div>
                  {item.tur && <span style={styles.turBadgeSmall}>{item.tur}</span>}
                  <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ marginLeft: 12, color: '#e67d21', fontSize: 14 }} />
                </div>

                {/* Accordion Content - Collapsible */}
                {isOpen && (
                  <>
                    {/* Photo Slider — yalnızca görsel varsa göster */}
                    {item.gorseller.length > 0 && (
                      <div
                        style={styles.photoWrap}
                        onTouchStart={(e) => handleTouchStart(item.gayrimenkulID, e)}
                        onTouchEnd={(e) => handleTouchEnd(item.gayrimenkulID, item.gorseller.length, e)}
                      >
                        <div style={styles.photoSlider}>
                          {item.gorseller.map((gorsel, i) => (
                            <img
                              key={i}
                              src={`${API_BASE}${gorsel.url}`}
                              alt={`${item.baslik} ${i + 1}`}
                              style={{
                                ...styles.photoSlide,
                                transform: `translateX(-${idx * 100}%)`,
                              }}
                              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ))}
                        </div>
                        {item.gorseller.length > 1 && (
                          <>
                            <button style={{ ...styles.photoBtn, left: 8 }} onClick={() => prevPhoto(item.gayrimenkulID, item.gorseller.length)}>
                              <i className="fas fa-chevron-left" />
                            </button>
                            <button style={{ ...styles.photoBtn, right: 8 }} onClick={() => nextPhoto(item.gayrimenkulID, item.gorseller.length)}>
                              <i className="fas fa-chevron-right" />
                            </button>
                            <div style={styles.photoCount}>{idx + 1} / {item.gorseller.length}</div>
                            <div style={styles.photoDots}>
                              {item.gorseller.map((_, i) => (
                                <span
                                  key={i}
                                  style={{
                                    ...styles.photoDot,
                                    ...(i === idx ? styles.photoDotActive : {})
                                  }}
                                  onClick={() => setPhotoIdx(prev => ({ ...prev, [item.gayrimenkulID]: i }))}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Card Body */}
                    <div style={styles.cardBody}>
                      {/* Detaylar Section */}
                      {item.detaylar && (
                        <div style={styles.detaylarSection}>
                          <div style={styles.detaylarTitle}>
                            <i className="fas fa-list" style={{ marginRight: 6, color: '#e67d21' }} />
                            Detaylar
                          </div>
                          <div style={styles.detaylarGrid}>
                            {item.detaylar.proje && (
                              <div style={styles.detayItem}>
                                <i className="fas fa-building" style={{ ...styles.detayIcon, color: '#e67d21' }} />
                                <div style={styles.detayContent}>
                                  <div style={styles.detayLabel}>Proje</div>
                                  <div style={styles.detayValue}>{item.detaylar.proje}</div>
                                </div>
                              </div>
                            )}
                            {item.detaylar.blok && (
                              <div style={styles.detayItem}>
                                <i className="fas fa-th-large" style={{ ...styles.detayIcon, color: '#3b82f6' }} />
                                <div style={styles.detayContent}>
                                  <div style={styles.detayLabel}>Blok</div>
                                  <div style={styles.detayValue}>{item.detaylar.blok}</div>
                                </div>
                              </div>
                            )}
                            {item.detaylar.kat && (
                              <div style={styles.detayItem}>
                                <i className="fas fa-layer-group" style={{ ...styles.detayIcon, color: '#8b5cf6' }} />
                                <div style={styles.detayContent}>
                                  <div style={styles.detayLabel}>Kat</div>
                                  <div style={styles.detayValue}>{item.detaylar.kat}</div>
                                </div>
                              </div>
                            )}
                            {item.detaylar.cephe && (
                              <div style={styles.detayItem}>
                                <i className="fas fa-compass" style={{ ...styles.detayIcon, color: '#10b981' }} />
                                <div style={styles.detayContent}>
                                  <div style={styles.detayLabel}>Cephe</div>
                                  <div style={styles.detayValue}>{item.detaylar.cephe}</div>
                                </div>
                              </div>
                            )}
                            {item.detaylar.manzara && (
                              <div style={styles.detayItem}>
                                <i className="fas fa-mountain" style={{ ...styles.detayIcon, color: '#14b8a6' }} />
                                <div style={styles.detayContent}>
                                  <div style={styles.detayLabel}>Manzara</div>
                                  <div style={styles.detayValue}>{item.detaylar.manzara}</div>
                                </div>
                              </div>
                            )}
                            {item.m2 != null && (
                              <div style={styles.detayItem}>
                                <i className="fas fa-ruler-combined" style={{ ...styles.detayIcon, color: '#6366f1' }} />
                                <div style={styles.detayContent}>
                                  <div style={styles.detayLabel}>Net m²</div>
                                  <div style={styles.detayValue}>{item.m2} m²</div>
                                </div>
                              </div>
                            )}
                            {item.brutM2 != null && (
                              <div style={styles.detayItem}>
                                <i className="fas fa-ruler" style={{ ...styles.detayIcon, color: '#ec4899' }} />
                                <div style={styles.detayContent}>
                                  <div style={styles.detayLabel}>Brüt m²</div>
                                  <div style={styles.detayValue}>{item.brutM2} m²</div>
                                </div>
                              </div>
                            )}
                            {item.detaylar.takipNo && (
                              <div style={styles.detayItem}>
                                <i className="fas fa-hashtag" style={{ ...styles.detayIcon, color: '#64748b' }} />
                                <div style={styles.detayContent}>
                                  <div style={styles.detayLabel}>Takip No</div>
                                  <div style={styles.detayValue}>{item.detaylar.takipNo}</div>
                                </div>
                              </div>
                            )}
                            {item.detaylar.adres && (
                              <div style={{ ...styles.detayItem, gridColumn: '1 / -1' }}>
                                <i className="fas fa-map-marker-alt" style={{ ...styles.detayIcon, color: '#ef4444' }} />
                                <div style={styles.detayContent}>
                                  <div style={styles.detayLabel}>Adres</div>
                                  <div style={styles.detayValue}>{item.detaylar.adres}</div>
                                </div>
                              </div>
                            )}
                            {(item.detaylar.il || item.detaylar.ilce || item.detaylar.mahalle) && (
                              <div style={{ ...styles.detayItem, gridColumn: '1 / -1' }}>
                                <i className="fas fa-location-dot" style={{ ...styles.detayIcon, color: '#f59e0b' }} />
                                <div style={styles.detayContent}>
                                  <div style={styles.detayLabel}>Konum</div>
                                  <div style={styles.detayValue}>
                                    {[item.detaylar.mahalle, item.detaylar.ilce, item.detaylar.il].filter(Boolean).join(' / ')}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Info row */}
                      <div style={styles.infoRow}>
                        {item.durum && (
                          <span style={styles.infoChip}>
                            <i className="fas fa-tag" style={{ marginRight: 4 }} />
                            {item.durum}
                          </span>
                        )}
                      </div>

                      {/* Price */}
                      {item.fiyat != null && (
                        <div style={styles.priceRow}>
                          <span style={styles.priceLabel}>Liste Fiyatı</span>
                          <span style={styles.priceValue}>{formatPara(item.fiyat)}</span>
                        </div>
                      )}

                      {/* Ozellikler - Properties */}
                      {item.ozellikler && item.ozellikler.length > 0 && (
                        <div style={styles.ozelliklerSection}>
                          <div style={styles.ozelliklerTitle}>
                            <i className="fas fa-star" style={{ marginRight: 6, color: '#e67d21' }} />
                            Özellikler
                          </div>
                          <div style={styles.ozelliklerWrap}>
                            {item.ozellikler.map((o, i) => (
                              <span key={i} style={styles.ozellikChip}>
                                <i className="fas fa-check" style={{ marginRight: 4, fontSize: 10 }} />
                                {o.ad}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Offer / Payment Plan - Collapsible, birden fazla teklif varsa yan sekmeler ile */}
                      {item.teklifler && item.teklifler.length > 0 && (() => {
                        const tabIdx = Math.min(activeTeklifTab[item.gayrimenkulID] ?? 0, item.teklifler.length - 1);
                        const aktifTeklif = item.teklifler[tabIdx];
                        return (
                          <div style={styles.teklifBox}>
                            <div style={{ display: 'flex', gap: 10 }}>
                              {item.teklifler.length > 1 && (
                                <div style={styles.teklifTabBar}>
                                  {item.teklifler.map((tk, i) => (
                                    <button
                                      key={tk.teklifID}
                                      style={{ ...styles.teklifTab, ...(i === tabIdx ? styles.teklifTabActive : {}) }}
                                      onClick={() => setActiveTeklifTab(prev => ({ ...prev, [item.gayrimenkulID]: i }))}
                                    >
                                      {tk.teklifAdi}
                                    </button>
                                  ))}
                                </div>
                              )}
                              <div style={{ flex: 1 }}>
                                <div style={styles.teklifRow}>
                                  <span><i className="fas fa-handshake" style={{ marginRight: 6 }} />Teklif Miktarı</span>
                                  <span style={styles.teklifValue}>{formatPara(aktifTeklif.teklifMiktari)}</span>
                                </div>
                                {aktifTeklif.ortalamaVadeGun != null && (
                                  <div style={styles.teklifRow}>
                                    <span><i className="fas fa-clock" style={{ marginRight: 6 }} />Ortalama Vade</span>
                                    <span style={{ fontWeight: 600 }}>{aktifTeklif.ortalamaVadeGun} gün</span>
                                  </div>
                                )}
                                {aktifTeklif.odemePlanKalemleri && aktifTeklif.odemePlanKalemleri.length > 0 && (
                                  <>
                                    <button
                                      style={styles.planToggle}
                                      onClick={() => togglePlan(item.gayrimenkulID)}
                                    >
                                      <i className={`fas fa-${isPlanOpen ? 'chevron-up' : 'chevron-down'}`} style={{ marginRight: 6 }} />
                                      Ödeme Planı {isPlanOpen ? 'Gizle' : 'Göster'}
                                    </button>
                                    {isPlanOpen && (
                                      <div style={styles.planTable}>
                                        {aktifTeklif.odemePlanKalemleri.map((k, i) => (
                                          <div key={i} style={styles.planRow}>
                                            <span style={styles.planSira}>{i + 1}</span>
                                            <span style={styles.planTarih}>{new Date(k.vadeTarihi).toLocaleDateString('tr-TR')}</span>
                                            <span style={styles.planMiktar}>{formatPara(k.miktar)}</span>
                                            {k.aciklama && <span style={styles.planAciklama}>{k.aciklama}</span>}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Customer reaction */}
                      <div style={styles.reactionSection}>
                    <div style={styles.reactionTitle}>
                      <i className="fas fa-comment-alt" style={{ marginRight: 7 }} />
                      Görüşünüz
                    </div>
                    <div style={styles.durumBtns}>
                      {(['Olumlu', 'Nötr', 'Olumsuz'] as const).map(d => (
                        <button
                          key={d}
                          style={{
                            ...styles.durumBtn,
                            ...(note.durum === d ? durumBtnActive(d) : {})
                          }}
                          onClick={() => setNote(item.gayrimenkulID, 'durum', note.durum === d ? '' : d)}
                        >
                          <i className={`fas fa-${d === 'Olumlu' ? 'thumbs-up' : d === 'Olumsuz' ? 'thumbs-down' : 'minus-circle'}`} style={{ marginRight: 5 }} />
                          {d}
                        </button>
                      ))}
                    </div>
                    <textarea
                      style={styles.noteArea}
                      placeholder="Notunuzu buraya yazabilirsiniz..."
                      value={note.not}
                      onChange={e => setNote(item.gayrimenkulID, 'not', e.target.value)}
                      rows={3}
                    />
                    <button
                      style={{ ...styles.saveBtn, ...(isSaved ? styles.saveBtnSaved : {}) }}
                      onClick={() => saveNote(item.gayrimenkulID)}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />Kaydediliyor...</>
                      ) : isSaved ? (
                        <><i className="fas fa-check" style={{ marginRight: 6 }} />Kaydedildi</>
                      ) : (
                        <><i className="fas fa-save" style={{ marginRight: 6 }} />Görüşümü Kaydet</>
                      )}
                    </button>
                  </div>
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </main>

      <footer style={styles.footer}>
        <i className="fas fa-lock" style={{ marginRight: 6, opacity: 0.6 }} />
        Bu sayfa size özel oluşturulmuştur.
      </footer>
    </div>
  );
}

// ---- Helpers ----
function durumBtnActive(d: string): React.CSSProperties {
  if (d === 'Olumlu') return { background: '#d4edda', borderColor: '#27ae60', color: '#155724' };
  if (d === 'Olumsuz') return { background: '#f8d7da', borderColor: '#dc3545', color: '#721c24' };
  return { background: '#e2e3e5', borderColor: '#6c757d', color: '#383d41' };
}

// ---- Styles ----
const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: '#f4f6f9', fontFamily: "'Segoe UI', sans-serif" },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 24 },
  header: { background: '#1a2440', color: '#fff', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 8px rgba(0,0,0,0.3)' },
  headerInner: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', maxWidth: 720, margin: '0 auto' },
  brand: { display: 'flex', alignItems: 'center', flexShrink: 0 },
  brandText: { fontWeight: 700, fontSize: 16, letterSpacing: 0.5 },
  sunumBaslik: { fontWeight: 600, fontSize: 14, lineHeight: 1.3 },
  cariAd: { fontSize: 11, opacity: 0.7, marginTop: 2 },
  expiry: { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, fontSize: 11, opacity: 0.85 },
  expiryWarning: { background: '#c0392b', padding: '6px 16px', fontSize: 12, textAlign: 'center' as const },
  main: { maxWidth: 720, margin: '0 auto', padding: '16px 12px 32px' },
  emptyState: { display: 'flex', flexDirection: 'column' as const, alignItems: 'center', padding: 60 },
  card: { background: '#fff', borderRadius: 12, marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' },
  accordionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 16px',
    cursor: 'pointer',
    borderBottom: '1px solid #f0f2f5',
    background: '#fafbfc',
    transition: 'background 0.2s'
  },
  accordionTitle: { fontSize: 16, fontWeight: 700, color: '#1a2440', lineHeight: 1.3 },
  accordionSub: { fontSize: 12, color: '#888', marginTop: 2 },
  turBadgeSmall: { 
    background: '#1a2440', 
    color: '#fff', 
    fontSize: 10, 
    padding: '3px 8px', 
    borderRadius: 6, 
    whiteSpace: 'nowrap' as const, 
    flexShrink: 0 
  },
  photoWrap: { position: 'relative', width: '100%', height: 280, background: '#e9ecef', overflow: 'hidden' },
  photoSlider: { 
    display: 'flex', 
    height: '100%', 
    transition: 'transform 0.3s ease-out',
    touchAction: 'pan-y'
  },
  photoSlide: { 
    minWidth: '100%', 
    height: '100%', 
    objectFit: 'cover' as const,
    transition: 'transform 0.3s ease-out'
  },
  photo: { width: '100%', height: '100%', objectFit: 'cover' as const },
  noPhoto: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' },
  photoBtn: {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none',
    borderRadius: '50%', width: 36, height: 36, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
    transition: 'background 0.2s',
    zIndex: 10
  },
  photoCount: {
    position: 'absolute', bottom: 48, right: 12,
    background: 'rgba(0,0,0,0.65)', color: '#fff',
    fontSize: 11, padding: '3px 10px', borderRadius: 12, fontWeight: 600
  },
  photoDots: {
    position: 'absolute',
    bottom: 12,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: 6,
    zIndex: 10
  },
  photoDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.4)',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  photoDotActive: {
    background: '#e67d21',
    width: 24,
    borderRadius: 4
  },
  cardBody: { padding: '14px 16px 16px' },
  detaylarSection: {
    background: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: 8,
    padding: '12px 14px',
    marginBottom: 12
  },
  detaylarTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1a2440',
    marginBottom: 10,
    display: 'flex',
    alignItems: 'center'
  },
  detaylarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 10
  },
  detayItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8
  },
  detayIcon: {
    fontSize: 16,
    marginTop: 2,
    flexShrink: 0
  },
  detayContent: {
    flex: 1,
    minWidth: 0
  },
  detayLabel: {
    fontSize: 10,
    color: '#94a3b8',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    marginBottom: 2,
    fontWeight: 600
  },
  detayValue: {
    fontSize: 13,
    color: '#1a2440',
    fontWeight: 600,
    lineHeight: 1.3,
    wordBreak: 'break-word' as const
  },
  titleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 },
  cardTitle: { fontSize: 16, fontWeight: 700, color: '#1a2440' },
  cardSub: { fontSize: 12, color: '#888', marginTop: 2 },
  turBadge: { background: '#1a2440', color: '#fff', fontSize: 11, padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap' as const, flexShrink: 0 },
  infoRow: { display: 'flex', gap: 8, flexWrap: 'wrap' as const, marginBottom: 8 },
  infoChip: { background: '#f0f2f5', color: '#555', fontSize: 12, padding: '3px 8px', borderRadius: 6 },
  priceRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f2f5', marginBottom: 10 },
  priceLabel: { fontSize: 12, color: '#888' },
  priceValue: { fontSize: 16, fontWeight: 700, color: '#1a2440' },
  teklifBox: { background: '#fff8f0', border: '1.5px solid #e67d21', borderRadius: 8, padding: '10px 12px', marginBottom: 10 },
  teklifRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#7a4010' },
  teklifValue: { fontWeight: 700, fontSize: 16, color: '#e67d21' },
  teklifTabBar: { display: 'flex', flexDirection: 'column' as const, gap: 4, flexShrink: 0, borderRight: '1px solid #fed7aa', paddingRight: 10, marginRight: 4 },
  teklifTab: { background: 'transparent', border: 'none', borderRadius: 6, padding: '5px 8px', fontSize: 11.5, fontWeight: 600, color: '#7a4010', cursor: 'pointer', textAlign: 'left' as const, whiteSpace: 'nowrap' as const },
  teklifTabActive: { background: '#e67d21', color: '#fff' },
  planToggle: {
    width: '100%',
    background: 'rgba(230, 125, 33, 0.1)',
    border: '1px solid #e67d21',
    borderRadius: 6,
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: 12,
    color: '#7a4010',
    fontWeight: 600,
    marginTop: 8,
    marginBottom: 8,
    transition: 'background 0.2s'
  },
  planHeader: { fontSize: 12, fontWeight: 600, color: '#7a4010', margin: '8px 0 6px', borderTop: '1px dashed #f0c080', paddingTop: 8 },
  planTable: { display: 'flex', flexDirection: 'column' as const, gap: 4, marginTop: 8 },
  planRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#555' },
  planSira: { background: '#e67d21', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, flexShrink: 0 },
  planTarih: { minWidth: 80, color: '#888' },
  planMiktar: { fontWeight: 600, color: '#1a2440', flex: 1, textAlign: 'right' as const },
  planAciklama: { color: '#aaa', fontSize: 11 },
  ozelliklerSection: {
    background: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 10
  },
  ozelliklerTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1a2440',
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center'
  },
  expandBtn: { width: '100%', background: 'none', border: '1px solid #dee2e6', borderRadius: 6, padding: '6px 0', cursor: 'pointer', fontSize: 12, color: '#555', marginBottom: 8 },
  ozelliklerWrap: { display: 'flex', flexWrap: 'wrap' as const, gap: 6 },
  ozellikChip: { background: '#e8f5e9', color: '#2e7d32', fontSize: 11, padding: '3px 8px', borderRadius: 6 },
  reactionSection: { borderTop: '1px solid #f0f2f5', marginTop: 10, paddingTop: 14 },
  reactionTitle: { fontSize: 13, fontWeight: 600, color: '#1a2440', marginBottom: 10 },
  durumBtns: { display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' as const },
  durumBtn: { flex: 1, minWidth: 90, padding: '8px 4px', border: '1.5px solid #dee2e6', borderRadius: 8, background: '#f8f9fa', cursor: 'pointer', fontSize: 13, color: '#555', fontWeight: 500 },
  noteArea: { width: '100%', border: '1.5px solid #dee2e6', borderRadius: 8, padding: '8px 10px', fontSize: 13, resize: 'vertical' as const, fontFamily: 'inherit', marginBottom: 10, boxSizing: 'border-box' as const },
  saveBtn: { width: '100%', background: '#e67d21', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  saveBtnSaved: { background: '#27ae60' },
  footer: { textAlign: 'center' as const, padding: '16px 0 24px', fontSize: 11, color: '#aaa' },
  
  // Proje Belgeleri Styles
  projeBelgeleriWrap: {
    background: 'linear-gradient(to bottom, #1a2440 0%, #2c3e50 100%)',
    padding: '20px 12px',
    borderBottom: '3px solid #e67d21'
  },
  projeBelgeleriContainer: {
    maxWidth: 720,
    margin: '0 auto'
  },
  projeBelgeleriTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#fff',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center'
  },
  belgeBolum: {
    background: '#fff',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12
  },
  belgeBolumBaslik: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1a2440',
    marginBottom: 10,
    display: 'flex',
    alignItems: 'center'
  },
  projePhotoWrap: {
    position: 'relative' as const,
    width: '100%',
    height: 250,
    borderRadius: 8,
    overflow: 'hidden',
    background: '#f0f2f5',
    touchAction: 'pan-y' as const
  },
  projePhotoSlider: {
    display: 'flex',
    height: '100%',
    width: '100%'
  },
  projePhotoSlide: {
    minWidth: '100%',
    height: '100%',
    transition: 'transform 0.3s ease',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative' as const
  },
  projePhotoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain' as const,
    cursor: 'pointer'
  },
  projePhotoAciklama: {
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    right: 0,
    background: 'rgba(0,0,0,0.7)',
    color: '#fff',
    fontSize: 12,
    padding: '6px 10px',
    textAlign: 'center' as const
  },
  projePhotoBtn: {
    position: 'absolute' as const,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(0,0,0,0.5)',
    color: '#fff',
    border: 'none',
    width: 40,
    height: 40,
    borderRadius: '50%',
    cursor: 'pointer',
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
    transition: 'background 0.2s'
  },
  projePhotoCount: {
    position: 'absolute' as const,
    top: 10,
    right: 10,
    background: 'rgba(0,0,0,0.6)',
    color: '#fff',
    fontSize: 12,
    padding: '4px 10px',
    borderRadius: 15,
    fontWeight: 600
  },
  projePhotoDots: {
    position: 'absolute' as const,
    bottom: 10,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: 6,
    zIndex: 2
  },
  projePhotoDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.5)',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  projePhotoDotActive: {
    background: '#fff',
    width: 24
  },
  belgeList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8
  },
  belgeLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    background: '#f8f9fa',
    borderRadius: 6,
    textDecoration: 'none',
    color: '#1a2440',
    border: '1px solid #e9ecef',
    transition: 'all 0.2s'
  },
  belgeLinkAd: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1a2440'
  },
  belgeLinkAciklama: {
    fontSize: 11,
    color: '#888',
    marginTop: 2
  }
};
